import { House, LogIn } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkAction } from "../../components/Screen.js";

import { SelectionPicker } from "../../components/game/SelectionPicker.js";
import {
  HttpError,
  completeSetup,
  fetchSetupState,
  type SetupState,
} from "../../services/http.js";

/**
 * First-run setup — `/setup`, phase 16g.
 *
 * Creates the very first admin on a fresh installation, then closes forever.
 * The standard bootstrap: Gitea, Discourse and Grafana all do this.
 *
 * # What it is *not* fixing
 *
 * ⚠️ The phase 16 plan said nothing could grant the admin role. That is wrong —
 * `sonolume-ingest role <email>` has existed since phase 13 and remains the
 * right tool for a second admin or a revocation. What was missing is an
 * **in-product** path: the CLI needs shell access to the host or container and
 * the database URL, which whoever just ran `docker compose up` may not have to
 * hand, and which cannot be the answer for an installation somebody else
 * operates.
 *
 * # Every failure looks the same, on purpose
 *
 * The server answers `404` for a closed installation *and* for a wrong token,
 * indistinguishably. So this page cannot say "wrong token" — it says the one
 * sentence that covers both and names where a deployer finds the real one. That
 * is the same argument `RecapPage` makes about a live game's 404.
 *
 * # Three states, and the middle one is not a spinner people stare at
 *
 * `null` while asking, `false` once the answer is "closed", the state object
 * when it is open. An empty form that flashed before the answer arrived would
 * invite somebody to start typing into a page that is about to say no.
 */
export function SetupPage() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  // Phase 16g-2. The installation's feed scope, chosen here and changeable in
  // `/admin` afterwards — which is why nothing here is required.
  const [offer, setOffer] = useState<SetupState | null>(null);
  const [genres, setGenres] = useState<string[]>([]);
  const [decades, setDecades] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    void fetchSetupState().then(
      (state) => {
        if (!cancelled) {
          setOpen(true);
          setOffer(state);
        }
      },
      () => {
        // Every failure is the same answer here — a closed installation, an
        // unreachable server, anything. None of them offer a form.
        if (!cancelled) setOpen(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (open === null) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <p className="text-sm text-white/50">{t("install.loading")}</p>
      </main>
    );
  }

  if (done) {
    return (
      <main className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-2xl font-semibold">{t("install.doneTitle")}</h1>
        <p className="text-sm text-white/60">{t("install.doneLead")}</p>
        <LinkAction to="/login" icon={LogIn} accent>
          {t("install.signIn")}
        </LinkAction>
      </main>
    );
  }

  if (!open) {
    return (
      <main className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-2xl font-semibold">{t("install.closedTitle")}</h1>
        <p className="text-sm text-white/60">{t("install.closedLead")}</p>
        <LinkAction to="/" icon={House}>
          {t("recap.home")}
        </LinkAction>
      </main>
    );
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void completeSetup({
      token,
      email,
      password,
      display_name: displayName,
      genres,
      decades,
    }).then(
      () => {
        setDone(true);
        setBusy(false);
      },
      (cause: unknown) => {
        // A 404 here means the token was wrong or somebody else finished setup
        // first, and the server will not say which. Anything else carries its
        // own message — a weak password, a malformed address.
        setError(
          cause instanceof HttpError && cause.code === "not_found"
            ? t("install.closedLead")
            : cause instanceof Error
              ? cause.message
              : t("install.closedLead"),
        );
        setBusy(false);
      },
    );
  };

  return (
    <main className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-5 p-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("install.title")}</h1>
        <p className="mt-1 text-sm text-white/60">{t("install.lead")}</p>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label={t("install.tokenLabel")}
          help={t("install.tokenHelp")}
          value={token}
          onChange={setToken}
          autoComplete="off"
        />
        <Field
          label={t("install.nameLabel")}
          value={displayName}
          onChange={setDisplayName}
          autoComplete="nickname"
        />
        <Field
          label={t("install.emailLabel")}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
        />
        <Field
          label={t("install.passwordLabel")}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
        />

        <section
          aria-label={t("scope.setupTitle")}
          className="border-t border-white/10 pt-4"
        >
          <h2 className="text-sm font-semibold">{t("scope.setupTitle")}</h2>
          <p className="mt-1 mb-3 text-xs text-white/50">{t("scope.setupLead")}</p>
          <SelectionPicker
            genres={offer?.genres ?? null}
            decades={offer?.decades ?? null}
            chosenGenres={genres}
            chosenDecades={decades}
            // No count yet: this runs before there is an admin, so there is
            // nothing authorised to ask the catalogue how big the answer is —
            // and a wrong number would be worse than none. `/admin` shows it.
            playableTracks={-1}
            disabled={busy}
            onChange={(next) => {
              setGenres(next.genres);
              setDecades(next.decades);
            }}
          />
        </section>

        {error && (
          <p role="alert" className="text-sm text-wrong-500">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="min-h-14 rounded-lg bg-flash-500 px-4 text-lg font-semibold text-stage-950 disabled:opacity-40"
        >
          {busy ? t("install.working") : t("install.submit")}
        </button>
      </form>
    </main>
  );
}

/** One labelled input. Local because nothing else on this route needs it. */
function Field({
  label,
  help,
  type = "text",
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  help?: string;
  type?: string;
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="min-h-12 rounded-lg border border-white/20 bg-stage-900 px-3 text-base"
      />
      {help && <span className="text-xs text-white/40">{help}</span>}
    </label>
  );
}
